/**
 * Browser -> R2, in two steps.
 *
 * The API never sees the bytes: it signs a PUT, the browser uploads straight
 * to the bucket, and what comes back is an object key to hang off a message.
 * XHR rather than fetch, because upload progress is the one thing fetch still
 * cannot report and a 20 MB video with no progress bar reads as a hang.
 */

import { useEffect, useState } from 'react';
import { api } from './api.js';
import { debug, debugTimer } from './debug.js';

export class UploadError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'UploadError';
    this.cause = cause;
  }
}

export function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

export const isImage = (mimeType) => String(mimeType ?? '').startsWith('image/');

function put(url, file, headers, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    for (const [name, value] of Object.entries(headers ?? {})) {
      xhr.setRequestHeader(name, value);
    }

    const done = debugTimer('upload', 'PUT to storage started', {
      name: file.name,
      size: file.size,
      type: file.type,
      // The origin only: the rest of the URL is the signature.
      host: safeHost(url),
    });
    // Progress fires many times a second on a fast link; logging every one
    // would bury everything else. Deciles are enough to see a stall.
    let lastDecile = -1;

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const fraction = event.loaded / event.total;
      const decile = Math.floor(fraction * 10);
      if (decile > lastDecile) {
        lastDecile = decile;
        debug('upload', `${decile * 10}%`, { name: file.name, loaded: event.loaded });
      }
      onProgress?.(fraction);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        done('PUT to storage succeeded', { status: xhr.status, name: file.name });
        resolve();
        return;
      }
      /**
       * The two failures worth naming, because they look identical in the UI
       * and have completely different fixes: a 403 usually means the signature
       * did not match what was actually sent (a header the browser changed, or
       * a URL that expired), and a status of 0 is almost always the bucket's
       * CORS rules refusing the request before it was made.
       */
      done('PUT to storage failed', {
        status: xhr.status,
        name: file.name,
        // R2 answers with an XML error document; the first part of it is the
        // part that says which rule was broken.
        response: String(xhr.responseText ?? '').slice(0, 400),
      });
      reject(new UploadError(`Storage rejected the upload (${xhr.status})`));
    };

    xhr.onerror = () => {
      done('PUT to storage could not be sent', {
        name: file.name,
        hint: 'usually the bucket CORS policy — it must allow PUT from this origin',
      });
      reject(new UploadError('The upload could not reach storage'));
    };

    xhr.onabort = () => {
      done('PUT to storage aborted', { name: file.name });
      reject(new UploadError('Upload cancelled'));
    };

    xhr.send(file);
  });
}

/** Origin of a presigned URL — safe to log, unlike the signature on the end. */
function safeHost(url) {
  try {
    return new URL(url).origin;
  } catch {
    return 'unparseable';
  }
}

/**
 * Uploads one file and returns the descriptor a message wants.
 *
 * `context: 'ticket'` files under the app instead of a thread, which is what
 * lets evidence be gathered before the ticket it belongs to exists.
 */
export async function uploadFile(
  appId,
  file,
  { conversationId, context = 'chat', onProgress } = {}
) {
  const finished = debugTimer('upload', 'picked a file', {
    name: file.name,
    size: file.size,
    type: file.type || '(none — will be sent as octet-stream)',
    context,
    conversationId,
  });

  let target;
  try {
    target = await api.uploads.sign(appId, {
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
      context,
      ...(context === 'chat' ? { conversationId } : {}),
    });
  } catch (err) {
    // A 503 here is an unconfigured bucket, not a broken file — worth telling
    // apart before anyone goes looking at the upload itself.
    finished('signing failed', { name: file.name, status: err.status, code: err.code });
    throw err;
  }

  debug('upload', 'signed', {
    key: target.key,
    expiresIn: target.expiresIn,
    publicBucket: Boolean(target.publicUrl),
  });

  await put(target.uploadUrl, file, target.headers, onProgress);
  finished('upload complete', { key: target.key, size: file.size });

  return {
    key: target.key,
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    // Present only on a public bucket; otherwise the viewer signs a URL when
    // it actually needs one.
    url: target.publicUrl ?? null,
  };
}

/**
 * A URL for reading an attachment. Public buckets answer instantly from the
 * message itself; private ones cost one round trip and expire in minutes,
 * which is why callers ask at the moment of use rather than caching it.
 */
export async function attachmentUrl(appId, attachment, { download = false } = {}) {
  if (attachment.url && !download) {
    debug('attachment', 'using the public URL from the message', { key: attachment.key });
    return attachment.url;
  }

  const done = debugTimer('attachment', 'signing a download link', {
    key: attachment.key,
    download,
  });
  try {
    const signed = await api.uploads.link(appId, attachment.key, { download });
    done('download link signed', { key: attachment.key, expiresIn: signed.expiresIn });
    return signed.url;
  } catch (err) {
    done('download link refused', { key: attachment.key, status: err.status, code: err.code });
    throw err;
  }
}

/**
 * Whether this server can store files at all, and how big they may be.
 *
 * Server-wide rather than per app — it is one bucket — so it comes from
 * `/config` and every composer reads the same answer. Defaults to disabled so
 * nothing offers a paperclip before the answer arrives.
 */
/**
 * The retention window, cached at module level.
 *
 * A leaf attachment row needs this one number to explain why a file is gone,
 * and threading it down four components to get there would be worse than a
 * module-level value that `useUploads` fills in once per session. Null until
 * the config lands, which reads as "do not mention a policy you cannot state".
 */
let retentionDays = null;
export const attachmentRetentionDays = () => retentionDays;

export function useUploads() {
  const [uploads, setUploads] = useState({ enabled: false, maxBytes: 0, retentionDays: null });

  useEffect(() => {
    let live = true;
    api
      .config()
      .then((config) => {
        if (!live) return;
        const resolved = {
          enabled: Boolean(config.storageEnabled),
          maxBytes: config.maxUploadBytes ?? 0,
          retentionDays: config.attachmentRetentionDays || null,
        };
        retentionDays = resolved.retentionDays;
        // The first thing to check when the paperclip is missing: the server
        // said storage is off, rather than the UI having hidden it.
        debug('upload', 'storage config', resolved);
        setUploads(resolved);
      })
      .catch((err) => debug('upload', 'could not read the storage config', { message: err.message }));
    return () => {
      live = false;
    };
  }, []);

  return uploads;
}

export default { uploadFile, attachmentUrl, formatBytes, isImage, useUploads };
